import { Col, Row } from "antd";
import dayjs from "dayjs";
import { get, set } from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdOutlineAddRoad,
  MdOutlineBadge,
  MdOutlineCake,
  MdOutlineFamilyRestroom,
  MdOutlineHealthAndSafety,
  MdOutlineMap,
  MdOutlinePinDrop,
  MdOutlineSouthAmerica,
  MdOutlineSubtitles,
  MdPeopleOutline,
} from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { useLazyQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { FRANCE_ADDRESS_ZIP_CODE_CITY } from "utils/api/graphql/queries/referentials";
import {
  CONTACT_POINT_SYSTEM,
  DATE_FORMAT,
  FAMILY_SITUATION,
  FRANCE_DEPARTMENTS,
  GENDER,
  MILTIS_PRODUCT_CODES,
  MILTIS_SOCIAL_REGIMES,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

const Adherent = ({
  setPercent,
  isFieldRequired,
  initialValues,
  bindField,
}) => {
  const [cities, setCities] = useState([]);
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const { t: p } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const COUNTRIES = p("DETAIL.COUNTRIES", { returnObjects: true });
  const SOCIAL_REGIMES_TRANSLATION = p("B2C_MILTIS.SOCIAL_REGIMES", {
    returnObjects: true,
  });
  const isLumineis =
    get(initialValues, "insurancePlan.product") ===
    MILTIS_PRODUCT_CODES.LUMINEIS;
  const birthCountry = get(
    initialValues,
    `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
  );
  const showFrenchBirthCoordinates = isLumineis && birthCountry === "France";
  const [addressZipCodeCity] = useLazyQuery(FRANCE_ADDRESS_ZIP_CODE_CITY);

  const fetchCities = (zipCode) => {
    if (!zipCode || zipCode.length !== 5) {
      setCities([]);
      return;
    }
    addressZipCodeCity({
      variables: { where: { zipCode } },
      onCompleted: ({ franceAddressZipCodeCity: { response } }) => {
        setCities(response);
      },
    });
  };

  useEffect(() => {
    if (!showFrenchBirthCoordinates) {
      setCities([]);
      return;
    }
    fetchCities(
      get(
        initialValues,
        `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_ZIP_CODE}.value`,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFrenchBirthCoordinates]);

  const genderPath = "project.contact.user.gender";
  const genderField = bindField(genderPath, {
    label: SUBSCRIPTION.FORM.GENDER.LABEL,
    required: isFieldRequired(genderPath),
    onChange: () => setPercent(initialValues),
  });

  const lastnamePath = "project.contact.user.lastname";
  const lastnameField = bindField(lastnamePath, {
    label: SUBSCRIPTION.FORM.LAST_NAME.LABEL,
    required: isFieldRequired(lastnamePath),
    disabled: true,
  });

  const birthNamePath = "project.contact.user.birthName";
  const birthNameField = bindField(birthNamePath, {
    label: SUBSCRIPTION.FORM.BIRTH_NAME.LABEL,
    required: isFieldRequired(birthNamePath),
    onBlur: () => setPercent(initialValues),
  });

  const firstnamePath = "project.contact.user.firstname";
  const firstnameField = bindField(firstnamePath, {
    label: SUBSCRIPTION.FORM.FIRST_NAME.LABEL,
    required: isFieldRequired(firstnamePath),
    disabled: true,
  });

  const birthDatePath = "project.contact.user.birthDate";
  const birthDateField = bindField(birthDatePath, {
    label: SUBSCRIPTION.FORM.BIRTH_DATE.LABEL,
    required: isFieldRequired(birthDatePath),
    disabled: true,
    onBlur: () => setPercent(initialValues),
  });

  const birthCountryPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`;
  const birthCountryField = bindField(birthCountryPath, {
    label: SUBSCRIPTION.FORM.BIRTH_COUNTRY.LABEL,
    required: isFieldRequired(birthCountryPath),
    onChange: (value) => {
      if (value !== "France") {
        set(
          initialValues,
          `telecoms.contact.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
          "Autre/Etranger",
        );
      } else {
        set(
          initialValues,
          `telecoms.contact.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
          null,
        );
      }
      setPercent(initialValues);
    },
    onBlur: () => setPercent(initialValues),
  });

  const birthDepartmentPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`;
  const birthDepartmentField = bindField(birthDepartmentPath, {
    label: SUBSCRIPTION.FORM.BIRTH_DEPARTMENT.LABEL,
    required: isFieldRequired(birthDepartmentPath),
    disabled:
      get(
        initialValues,
        `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
      ) !== "France",
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  const birthZipCodePath = `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_ZIP_CODE}.value`;
  const birthZipCodeField = bindField(birthZipCodePath, {
    label: SUBSCRIPTION.FORM.BIRTH_ZIP_CODE.LABEL,
    required: isFieldRequired(birthZipCodePath),
    extractValue: ({ target: { value } }) =>
      value.replace(/\D/g, "").slice(0, 5),
    onChange: (zipCode) => {
      set(
        initialValues,
        `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`,
        null,
      );
      fetchCities(zipCode);
    },
    onBlur: () => setPercent(initialValues),
  });

  const birthCityPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`;
  const birthCityField = bindField(birthCityPath, {
    label: SUBSCRIPTION.FORM.BIRTH_CITY.LABEL,
    required: isFieldRequired(birthCityPath),
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  const socialRegimePath = "project.contact.socialRegime";
  const socialRegimeField = bindField(socialRegimePath, {
    label: SUBSCRIPTION.FORM.MANDATORY_SCHEME.LABEL,
    required: isFieldRequired(socialRegimePath),
    disabled: true,
  });

  const socialSecurityNumberPath = "project.contact.socialSecurityNumber";
  const socialSecurityNumberField = bindField(socialSecurityNumberPath, {
    label: SUBSCRIPTION.FORM.SOCIAL_SECURITY_NUMBER.LABEL,
    required: isFieldRequired(socialSecurityNumberPath),
    onBlur: () => setPercent(initialValues),
  });

  const familySituationPath = "project.contact.familySituation";
  const familySituationField = bindField(familySituationPath, {
    label: SUBSCRIPTION.FORM.FAMILY_SITUATION.LABEL,
    required: isFieldRequired(familySituationPath),
    onChange: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.ADHERENT}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ADHERENT.TITLE}
        subtitle={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ADHERENT.SUBTITLE}
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <FormField
              path={genderField.path}
              label={genderField.label}
              required={genderField.required}
              disabled={genderField.disabled}
            >
              {(field) => (
                <Input
                  type="radio"
                  variant="group"
                  icon={<MdPeopleOutline icon="man" size={16} />}
                  values={[
                    {
                      value: GENDER.MALE,
                      label: SUBSCRIPTION.FORM.GENDER.OPTIONS.MR,
                    },
                    {
                      value: GENDER.FEMALE,
                      label: SUBSCRIPTION.FORM.GENDER.OPTIONS.MRS,
                    },
                  ]}
                  label={genderField.label}
                  value={get(initialValues, genderPath)}
                  {...field}
                  onChange={genderField.onChange}
                  onBlur={genderField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={lastnameField.path}
              label={lastnameField.label}
              required={lastnameField.required}
              disabled={lastnameField.disabled}
            >
              {(field) => (
                <Input
                  placeholder={SUBSCRIPTION.FORM.LAST_NAME.PLACEHOLDER}
                  label={lastnameField.label}
                  icon={<MdOutlineBadge size={16} />}
                  defaultValue={lastnameField.defaultValue}
                  {...field}
                  onChange={lastnameField.onChange}
                  onBlur={lastnameField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={birthNameField.path}
              label={birthNameField.label}
              required={birthNameField.required}
              disabled={birthNameField.disabled}
            >
              {(field) => (
                <Input
                  placeholder={SUBSCRIPTION.FORM.BIRTH_NAME.PLACEHOLDER}
                  label={birthNameField.label}
                  icon={<MdOutlineBadge size={16} />}
                  type="custom"
                  normalizeText={true}
                  defaultValue={birthNameField.defaultValue}
                  {...field}
                  onChange={birthNameField.onChange}
                  onBlur={birthNameField.onBlur}
                  optional={!birthNameField.required}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={firstnameField.path}
              label={firstnameField.label}
              required={firstnameField.required}
              disabled={firstnameField.disabled}
            >
              {(field) => (
                <Input
                  label={firstnameField.label}
                  icon={<MdOutlineBadge size={16} />}
                  placeholder={SUBSCRIPTION.FORM.FIRST_NAME.PLACEHOLDER}
                  defaultValue={firstnameField.defaultValue}
                  {...field}
                  onChange={firstnameField.onChange}
                  onBlur={firstnameField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={birthDateField.path}
              label={birthDateField.label}
              required={birthDateField.required}
              disabled={birthDateField.disabled}
            >
              {(field) => (
                <Input
                  placeholder={SUBSCRIPTION.FORM.BIRTH_DATE.PLACEHOLDER}
                  type="date"
                  variant="simple"
                  label={birthDateField.label}
                  defaultValue={dayjs(get(initialValues, birthDatePath))}
                  icon={<MdOutlineCake size={16} />}
                  format={DATE_FORMAT}
                  {...field}
                  onChange={birthDateField.onChange}
                  onBlur={birthDateField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={birthCountryField.path}
              label={birthCountryField.label}
              required={birthCountryField.required}
              disabled={birthCountryField.disabled}
            >
              {(field) => (
                <Select
                  icon={<MdOutlineMap size={16} />}
                  label={birthCountryField.label}
                  placeholder={SUBSCRIPTION.FORM.BIRTH_COUNTRY.PLACEHOLDER}
                  showSearch
                  options={COUNTRIES.map((name) => ({
                    value: name,
                    label: name,
                  }))}
                  value={get(initialValues, birthCountryPath)}
                  defaultValue={birthCountryField.defaultValue}
                  {...field}
                  onChange={birthCountryField.onChange}
                  onBlur={birthCountryField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={birthDepartmentField.path}
              label={birthDepartmentField.label}
              required={birthDepartmentField.required}
              disabled={birthDepartmentField.disabled}
            >
              {(field) => (
                <Select
                  label={birthDepartmentField.label}
                  icon={<MdOutlineAddRoad size={16} />}
                  placeholder={SUBSCRIPTION.FORM.BIRTH_DEPARTMENT.PLACEHOLDER}
                  showSearch
                  allowClear
                  options={FRANCE_DEPARTMENTS.map(({ code, name }) => ({
                    label: `${code ? `(${code})` : ""} ${name}`,
                    value: name,
                  }))}
                  value={get(initialValues, birthDepartmentPath)}
                  defaultValue={birthDepartmentField.defaultValue}
                  {...field}
                  onChange={birthDepartmentField.onChange}
                  onBlur={birthDepartmentField.onBlur}
                />
              )}
            </FormField>
          </Col>
          {showFrenchBirthCoordinates && (
            <>
              <Col lg={24} xs={24}>
                <FormField
                  path={birthZipCodeField.path}
                  label={birthZipCodeField.label}
                  required={birthZipCodeField.required}
                  disabled={birthZipCodeField.disabled}
                >
                  {(field) => (
                    <Input
                      label={birthZipCodeField.label}
                      icon={<MdOutlinePinDrop size={16} />}
                      placeholder={SUBSCRIPTION.FORM.BIRTH_ZIP_CODE.PLACEHOLDER}
                      defaultValue={birthZipCodeField.defaultValue}
                      {...field}
                      onChange={birthZipCodeField.onChange}
                      onBlur={birthZipCodeField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
              <Col lg={24} xs={24}>
                <FormField
                  path={birthCityField.path}
                  label={birthCityField.label}
                  required={birthCityField.required}
                  disabled={birthCityField.disabled}
                >
                  {(field) => (
                    <Select
                      label={birthCityField.label}
                      icon={<MdOutlineSouthAmerica size={16} />}
                      placeholder={SUBSCRIPTION.FORM.BIRTH_CITY.PLACEHOLDER}
                      showSearch
                      options={cities}
                      value={get(initialValues, birthCityPath)}
                      defaultValue={birthCityField.defaultValue}
                      {...field}
                      onChange={birthCityField.onChange}
                      onBlur={birthCityField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
            </>
          )}
          <Col lg={24} xs={24}>
            <FormField
              path={socialRegimeField.path}
              label={socialRegimeField.label}
              required={socialRegimeField.required}
              disabled={socialRegimeField.disabled}
            >
              {(field) => (
                <Select
                  icon={<MdOutlineHealthAndSafety size={16} />}
                  options={Object.values(MILTIS_SOCIAL_REGIMES).map((option) => {
                    const key = Object.keys(MILTIS_SOCIAL_REGIMES).find(
                      (k) => MILTIS_SOCIAL_REGIMES[k] === option,
                    );
                    return {
                      label: SOCIAL_REGIMES_TRANSLATION[key] || option,
                      value: option,
                    };
                  })}
                  label={socialRegimeField.label}
                  placeholder={SUBSCRIPTION.FORM.MANDATORY_SCHEME.PLACEHOLDER}
                  defaultValue={socialRegimeField.defaultValue}
                  {...field}
                  onChange={socialRegimeField.onChange}
                  onBlur={socialRegimeField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={socialSecurityNumberField.path}
              label={socialSecurityNumberField.label}
              required={socialSecurityNumberField.required}
              disabled={socialSecurityNumberField.disabled}
            >
              {(field) => (
                <Input
                  label={socialSecurityNumberField.label}
                  placeholder={
                    SUBSCRIPTION.FORM.SOCIAL_SECURITY_NUMBER.PLACEHOLDER
                  }
                  icon={<MdOutlineSubtitles size={16} />}
                  type="custom"
                  trim={true}
                  defaultValue={socialSecurityNumberField.defaultValue}
                  {...field}
                  onChange={socialSecurityNumberField.onChange}
                  onBlur={socialSecurityNumberField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={familySituationField.path}
              label={familySituationField.label}
              required={familySituationField.required}
              disabled={familySituationField.disabled}
            >
              {(field) => (
                <Select
                  label={familySituationField.label}
                  icon={<MdOutlineFamilyRestroom size={16} />}
                  placeholder={SUBSCRIPTION.FORM.FAMILY_SITUATION.PLACEHOLDER}
                  type="simple"
                  options={Object.entries(FAMILY_SITUATION).map(
                    ([key, value]) => ({
                      value,
                      label: SUBSCRIPTION.FORM.FAMILY_SITUATION.OPTIONS[key],
                    }),
                  )}
                  defaultValue={familySituationField.defaultValue}
                  {...field}
                  onChange={familySituationField.onChange}
                  onBlur={familySituationField.onBlur}
                />
              )}
            </FormField>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default Adherent;
